/**
 * The storefront's session counter, on Shopify storefronts.
 *
 * The same contract as the storefront's SiteSessionProvider
 * (cosmetic-website src/API/sessionProvider.tsx), so both channels fill
 * `site_sessions` the same way: one session per tab, kept in sessionStorage; a
 * new one after 30 minutes without activity; Start on its first page with the
 * landing page, referrer, UTMs and device; then an Update with page count, exit
 * page and duration on every page load, every 7 seconds — hidden tabs included,
 * exactly as the storefront pings — and at once when the tab is back in view.
 *
 * The session id is the one this SDK has always stamped on cart events
 * (`storedum_session_uuid`), so the add-to-carts it already reports join these
 * sessions in the funnel.
 */
import type { SessionStartInput, SessionUpdateInput } from "@storedum/core";

const SESSION_KEY = "storedum_session_uuid";
const START_KEY = "storedum_session_start";
const LAST_KEY = "storedum_session_last";
const PAGES_KEY = "storedum_session_pages";
/** The session id the store has acknowledged a Start for. */
const RECORDED_KEY = "storedum_session_recorded";
const VISITOR_KEY = "storedum_vid";

export const INACTIVITY_MS = 30 * 60 * 1000;
export const HEARTBEAT_MS = 7000;

/** Private mode can refuse storage outright; the tab still keeps one session in memory. */
const memory: Record<string, string> = {};

function read(key: string, local?: boolean): string | null {
    try {
        const value = (local ? window.localStorage : window.sessionStorage).getItem(key);
        if (value != null) return value;
    } catch {
        /* fall back to memory */
    }
    return memory[key] ?? null;
}

function write(key: string, value: string, local?: boolean): void {
    memory[key] = value;
    try {
        (local ? window.localStorage : window.sessionStorage).setItem(key, value);
    } catch {
        /* private mode — memory keeps it for this page */
    }
}

export function newId(): string {
    try {
        if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
    } catch {
        /* not a secure context */
    }
    return `s_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
}

export interface SessionState {
    id: string;
    start: number;
    pages: number;
}

/**
 * The session this page load has already been counted in. A page view counts
 * once per session: events read the session id too (a product view, the cart
 * sync — anything carrying attribution), often before the counter's own first
 * tick, and the page they mint a session on is already that session's page 1.
 * Module state, so it is per page load: every Shopify page is a fresh load.
 */
let pageCountedIn: string | null = null;

/**
 * The tab's session as of `now`, which counts as activity. After 30 minutes
 * without any, it is a new session — the storefront's rule, to the millisecond
 * (30 minutes exactly is already idle). `page` counts this page load's view,
 * once.
 */
export function touchSession(now: number, page = false): SessionState {
    let id = read(SESSION_KEY);
    let start = Number(read(START_KEY)) || 0;
    const last = Number(read(LAST_KEY)) || 0;
    let pages = Number(read(PAGES_KEY)) || 0;
    if (!id || (last && now - last >= INACTIVITY_MS)) {
        // The page the shopper is on is the new session's first, whatever
        // touched it first — this page's own count, an event reading the id,
        // or a heartbeat in a tab back from 30 idle minutes — as the
        // storefront counts it.
        id = newId();
        start = now;
        pages = 1;
        pageCountedIn = id;
        write(SESSION_KEY, id);
    } else if (page && pageCountedIn !== id) {
        pages += 1;
        pageCountedIn = id;
    }
    // An id kept from before this counter existed starts its clock now.
    if (!start) start = now;
    write(START_KEY, String(start));
    write(LAST_KEY, String(now));
    write(PAGES_KEY, String(pages));
    return { id, start, pages };
}

/** The tab's session id as stored, without touching it: reading it here never starts a new one. */
export function storedSessionId(): string | null {
    return read(SESSION_KEY);
}

/** A first-party visitor id on the merchant's own origin; it outlives the tab. */
export function visitorId(): string {
    let id = read(VISITOR_KEY, true);
    if (!id) {
        id = newId();
        write(VISITOR_KEY, id, true);
    }
    return id;
}

/**
 * Device, OS and browser from the user agent, under the storefront's names
 * ("MacOS", …) — with the cases its parser read wrong put right: Android was
 * Linux, iOS was MacOS, Edge was Chrome and an iPad a desktop. Instagram's and
 * Facebook's in-app browsers are named, as on the storefront.
 */
export function deviceInfo(ua: string, touchPoints = 0): { device_type: string; os: string; browser: string } {
    const iPadOS = /Macintosh/.test(ua) && touchPoints > 1;
    const tablet = iPadOS || /iPad|Tablet|PlayBook|Silk/.test(ua) || (/Android/.test(ua) && !/Mobile/.test(ua));
    const mobile = !tablet && /Mobi|Android|iPhone|iPod|Windows Phone/.test(ua);
    const os = /Windows/.test(ua)
        ? "Windows"
        : iPadOS || /iPhone|iPad|iPod/.test(ua)
          ? "iOS"
          : /Android/.test(ua)
            ? "Android"
            : /CrOS/.test(ua)
              ? "ChromeOS"
              : /Mac OS X|Macintosh/.test(ua)
                ? "MacOS"
                : /Linux/.test(ua)
                  ? "Linux"
                  : "Unknown";
    const browser = /Instagram/.test(ua)
        ? "Instagram"
        : /FBAN|FBAV|FB_IAB/.test(ua)
          ? "Facebook"
          : /Edg(e|A|iOS)?\//.test(ua)
            ? "Edge"
            : /OPR\/|Opera/.test(ua)
              ? "Opera"
              : /SamsungBrowser/.test(ua)
                ? "Samsung Internet"
                : /CriOS|Chrome\//.test(ua)
                  ? "Chrome"
                  : /FxiOS|Firefox\//.test(ua)
                    ? "Firefox"
                    : /Safari\//.test(ua)
                      ? "Safari"
                      : "Unknown";
    return { device_type: tablet ? "tablet" : mobile ? "mobile" : "desktop", os, browser };
}

const BOTS = /bot|crawl|spider|slurp|lighthouse|headless|phantom|prerender|pingdom|gtmetrix/i;

/** A real shopper's visit — not the theme editor previewing the store, not a crawler or automation. */
export function realVisit(): boolean {
    try {
        if ((window as any).Shopify?.designMode) return false;
        return !navigator.webdriver && !BOTS.test(navigator.userAgent || "");
    } catch {
        return false;
    }
}

export interface SessionTransport {
    start(input: SessionStartInput): Promise<{ recorded?: boolean } | undefined>;
    update(input: SessionUpdateInput): Promise<{ recorded?: boolean; missing?: boolean } | undefined>;
}

function utm(params: URLSearchParams, key: string, cap: number): string | undefined {
    return (params.get(`utm_${key}`) || "").trim().slice(0, cap) || undefined;
}

/** The storefront's Start body, read from this page. */
export function startPayload(state: SessionState): SessionStartInput {
    const params = new URLSearchParams(location.search);
    return {
        session_uuid: state.id,
        visitor_id: visitorId(),
        landing_page: location.pathname,
        referrer: (document.referrer || "").slice(0, 1000),
        utm_source: utm(params, "source", 150),
        utm_medium: utm(params, "medium", 150),
        utm_campaign: utm(params, "campaign", 250),
        utm_term: utm(params, "term", 250),
        utm_content: utm(params, "content", 250),
        ...deviceInfo(navigator.userAgent || "", navigator.maxTouchPoints || 0),
        screen_width: screen.width,
        screen_height: screen.height,
    };
}

/**
 * Run the counter on this page: the page view now, a heartbeat every 7 s, and
 * one more the moment the tab is visible again. One request at a time — a beat
 * that finds the last one still in flight is skipped, not queued.
 */
export function trackSession(transport: SessionTransport, now: () => number = Date.now): () => void {
    let busy = false;
    const pulse = (page: boolean) => {
        const state = touchSession(now(), page);
        if (busy) return;
        busy = true;
        const sent =
            read(RECORDED_KEY) === state.id
                ? transport
                      .update({
                          session_uuid: state.id,
                          page_count: state.pages,
                          exit_page: location.pathname,
                          duration_seconds: Math.max(0, Math.floor((now() - state.start) / 1000)),
                      })
                      .then((reply) => {
                          // The store never saw this session (its Start was lost): start it again.
                          if (reply?.missing) write(RECORDED_KEY, "");
                      })
                : transport.start(startPayload(state)).then((reply) => {
                      if (reply?.recorded) write(RECORDED_KEY, state.id);
                  });
        sent.catch(() => undefined).then(() => {
            busy = false;
        });
    };
    pulse(true);
    const timer = setInterval(() => pulse(false), HEARTBEAT_MS);
    const onVisible = () => {
        if (document.visibilityState === "visible") pulse(false);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
        clearInterval(timer);
        document.removeEventListener("visibilitychange", onVisible);
    };
}
